import {
  Drawer,
  Space,
  Button,
  Form,
  Radio,
  Col,
  Row,
  Card,
  message,
  Spin,
} from "antd";
import { useForm, useFieldArray } from "react-hook-form";
import {
  RadioGroupFI,
  TextInputFI,
  NumberInputFI,
  TextAreaInputFI,
  DraggerFI,
  RichTextBoxFI,
  SwitchFI,
} from "../../../components/commons/HookFormInputs";
import { useEffect, useState } from "react";
import {
  PlusOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import postService from "../../../services/postService";

const PostFormDrawer = (props) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues: {
      type: "BLOG",
      isPublishing: false,
      order: 1,
      title: "",
      slug: "",
      tags: "",
      description: "",
      year: "",
      location: "",
      myRole: "",
      myTeam: "",
      image: null,
      ...props.data,
      content: props?.data?.content?.map((item) => ({
        ...item,
        note: item.note || "",
      })),
    },
  });

  const [messageApi, contextHolder] = message.useMessage();

  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: "content",
  });

  const title = watch("title");
  const type = watch("type");
  const [isLoading, setIsLoading] = useState(false);
  const [isFull, setIsFull] = useState(false);

  const convertViToEn = (str, toUpperCase = false) => {
    str = str.toLowerCase();
    str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
    str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
    str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
    str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
    str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
    str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
    str = str.replace(/đ/g, "d");
    // Some system encode vietnamese combining accent as individual utf-8 characters
    str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
    str = str.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
    str = str.replace(/[^\w\s]/gi, "");
    str = str.trim();
    str = str.replace(/[\s]/gi, "-");
    str = str.replace(/-+/g, "-");

    return toUpperCase ? str.toUpperCase() : str;
  };

  useEffect(() => {
    if (!props.data) {
      setValue("slug", convertViToEn(title));
    }
  }, [title, setValue]);

  const handleAddContentImage = () => {
    append({
      image: null,
      type: "IMAGE",
      view: "NORMAL",
      source: "",
      note: "",
    });
  };

  const handleChangeView = () => {
    setIsFull(!isFull);
  };

  const handleAddContentText = () => {
    append({ text: "", type: "TEXT" });
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    if (props.data) {
      postService
        .updatePost(data)
        .then(() => {
          setIsLoading(false);
          props.onSuccess("Update");
        })
        .catch((error) => {
          setIsLoading(false);
          messageApi.open({
            type: "error",
            content: error,
          });
        });
    } else {
      postService
        .createPost(data)
        .then(() => {
          setIsLoading(false);
          props.onSuccess("Create");
        })
        .catch((error) => {
          setIsLoading(false);
          messageApi.open({
            type: "error",
            content: error,
          });
        });
    }
  };

  return (
    <Drawer
      width={isFull ? "100%" : "60%"}
      title={props.data ? "Update post" : "Create a new post"}
      onClose={props.onClose}
      placement="right"
      open={true}
      maskClosable={false}
      extra={
        <Space>
          <Button
            onClick={handleChangeView}
            icon={isFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          />
          <Button onClick={props.onClose}>Cancel</Button>
          <Button
            disabled={!isValid || isLoading || !isDirty}
            onClick={handleSubmit(onSubmit)}
            type="primary"
          >
            {props.data ? "Update" : "Create"}
          </Button>
        </Space>
      }
    >
      {contextHolder}
      <Spin spinning={isLoading}>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <RadioGroupFI
                control={control}
                name="type"
                label="Type Post"
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button
                  disabled={!!props.data && type !== "BLOG"}
                  value="BLOG"
                >
                  Blog
                </Radio.Button>
                <Radio.Button
                  disabled={!!props.data && type !== "MY_WORKS"}
                  value="MY_WORKS"
                >
                  My works
                </Radio.Button>
              </RadioGroupFI>
            </Col>
            <Col span={8}>
              <NumberInputFI
                control={control}
                name="order"
                placeholder="Order"
                label="Order"
                rules={{ required: true }}
                required
                min={1}
                max={9999999}
              />
            </Col>
            <Col span={8}>
              <SwitchFI
                control={control}
                label="Publishing"
                name="isPublishing"
              />
            </Col>
            <Col span={24}>
              <TextInputFI
                control={control}
                name="title"
                placeholder="Title post input"
                label="Title"
                rules={{ required: true }}
                required
              />
            </Col>
            <Col span={24}>
              <TextInputFI
                control={control}
                disabled={true}
                name="slug"
                placeholder="Slug input (ex: mam-muoi-application-20-11)"
                label="Slug"
              />
            </Col>
            {type === "MY_WORKS" && (
              <Col span={24}>
                <TextAreaInputFI
                  rules={{ required: true }}
                  required
                  control={control}
                  name="description"
                  placeholder="Description input"
                  label="Description"
                  rows={3}
                />
              </Col>
            )}
            <Col span={24}>
              <TextInputFI
                control={control}
                name="tags"
                placeholder="Tags input (ex: My project, 2002)"
                label="Tags"
              />
            </Col>
            {type === "BLOG" && (
              <Col span={12}>
                <TextInputFI
                  control={control}
                  required
                  name="location"
                  placeholder="Location input"
                  label="Location"
                  rules={{ required: true }}
                />
              </Col>
            )}
            {type === "BLOG" && (
              <Col span={12}>
                <TextInputFI
                  required
                  control={control}
                  name="year"
                  placeholder="Year input"
                  label="Year"
                  rules={{ required: true }}
                />
              </Col>
            )}
            {type === "MY_WORKS" && (
              <Col span={12}>
                <TextAreaInputFI
                  control={control}
                  name="myRole"
                  placeholder="Role list input (ex: Research, Wireframe, Low and Hi-fi design)"
                  label="My Role"
                  rows={4}
                />
              </Col>
            )}
            {type === "MY_WORKS" && (
              <Col span={12}>
                <TextAreaInputFI
                  control={control}
                  name="myTeam"
                  placeholder="Team list input (ex: Instructor: Ms. Kieu Anh, Icon design:Duong Gia Hieu)"
                  label="My Team"
                  rows={4}
                />
              </Col>
            )}
            <Col span={24}>
              <DraggerFI
                control={control}
                name="image"
                label="Post Image"
                required
                rules={{ required: true }}
              />
            </Col>
            <Col span={24}>
              <Form.Item label={<b>Content</b>}>
                {fields.map((field, index) => {
                  if (field.type === "IMAGE") {
                    return (
                      <Card
                        type="inner"
                        key={field.id}
                        size="small"
                        title={`Section ${index + 1}`}
                        extra={
                          <Space>
                            <Button disabled={index === 0} icon={<ArrowUpOutlined />} onClick={() => swap(index, index - 1)}>
                            </Button>
                            <Button disabled={index === (fields.length - 1)} icon={<ArrowDownOutlined />} onClick={() => swap(index, index + 1)}>
                            </Button>
                            <Button
                              onClick={() => {
                                remove(index);
                              }}
                              icon={<DeleteOutlined />}
                              danger
                            >
                            </Button>
                          </Space>
                        }
                        style={{ marginBottom: 16 }}
                      >
                        <DraggerFI
                          control={control}
                          name={`content.${index}.image`}
                          label="Image"
                          required
                          rules={{ required: true }}
                        />

                        <Row gutter={16}>
                          <Col span={24}>
                            <RadioGroupFI
                              control={control}
                              name={`content.${index}.view`}
                              label=""
                            >
                              <Radio.Button value="NORMAL">Normal</Radio.Button>
                              <Radio.Button value="WIDE">Wide</Radio.Button>
                              <Radio.Button value="FULL">Full</Radio.Button>
                            </RadioGroupFI>
                          </Col>
                          {type === "BLOG" && (
                            <Col span={12}>
                              <TextInputFI
                                control={control}
                                name={`content.${index}.source`}
                                placeholder="Source input"
                                label="Source"
                                rules={{ required: false }}
                              />
                            </Col>
                          )}
                          <Col span={12}>
                            <TextInputFI
                              control={control}
                              name={`content.${index}.note`}
                              placeholder="Note input"
                              label="Note"
                              rules={{ required: false }}
                            />
                          </Col>
                        </Row>
                      </Card>
                    );
                  }
                  return (
                    <Card
                      type="inner"
                      key={field.id}
                      size="small"
                      title={`Section ${index + 1}`}
                      extra={
                        <Space>
                          <Button disabled={index === 0} icon={<ArrowUpOutlined />} onClick={() => swap(index, index - 1)}>
                          </Button>
                          <Button disabled={index === (fields.length - 1)} icon={<ArrowDownOutlined />} onClick={() => swap(index, index + 1)}>
                          </Button>
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => {
                              remove(index);
                            }}
                          >
                          </Button>
                        </Space>

                      }
                      style={{ marginBottom: 16 }}
                    >
                      <RichTextBoxFI
                        control={control}
                        name={`content.${index}.text`}
                        label="Text"
                        required
                        rules={{ required: true }}
                      />
                    </Card>
                  );
                })}

                <Row gutter={36}>
                  <Col span={12}>
                    <Button
                      block
                      icon={<PlusOutlined />}
                      type="dashed"
                      size={"large"}
                      onClick={handleAddContentImage}
                    >
                      Add Image
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      icon={<PlusOutlined />}
                      type="dashed"
                      size={"large"}
                      onClick={handleAddContentText}
                    >
                      Add Text
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default PostFormDrawer;
